import { Button, Input, Skeleton, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';

import LoadingComponent from 'components/loadingComponent/loadingComponent';
import dayjs from 'dayjs';
import Icon from 'utils/icon';
import { Link } from 'react-router-dom';
import TableCustomCell from 'components/tableCustomCell/tableCustomCell';
import { User } from 'modules/users/models/user';
import NotificationService from 'core/services/notification';
import { onDeleteUser, onGetUsers } from 'modules/users/services/users';
import AuthActionComponent from 'components/authAction/authAction';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { isRTL } from 'core/services/language';
import NoData from 'components/noData/noData';

function ListUserPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [users, setUsers] = useState<User[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedUser, setTempDeletedUser] = useState<number>();
	const [writingTimeout, setWritingTimeout] = useState<any>();
	const [search, setSearch] = useState<string>('');

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	useEffect(() => {
		clearTimeout(writingTimeout);

		setWritingTimeout(
			setTimeout(() => {
				if (search) {
					setCurrentPage(1);
					getUsers(1, search.toUpperCase());
				} else {
					getUsers();
				}
			}, 500),
		);
	}, [search]);

	const columns: TableProps<User>['columns'] = [
		{
			title: t('USERS.CLIENT_ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('USERS.CLIENT'),
			dataIndex: 'name',
			key: 'name',
			render: (id, user) => (
				<TableCustomCell
					title={isRTL() ? user.name?.ar : user.name?.en}
					subTitle={'+' + user.phone_country_code + ' ' + user.phone_number}
					subTitleStyles={{ direction: 'ltr', display: 'inline-block' }}
				/>
			),
		},
		{
			title: t('USERS.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
			render: (national_id) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{national_id}</span>,
		},
		// {
		// 	title: t('USERS.EMAIL'),
		// 	dataIndex: 'email',
		// 	key: 'email',
		// 	render: (email) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{email}</span>,
		// },
		{
			title: t('USERS.REGISTRY_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at) => (
				<span style={{ fontWeight: 500, color: '#7E8299' }}>{dayjs(created_at).format('DD MMM YYYY - HH:mm A')}</span>
			),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'profile_status',
			key: 'profile_status',
			render: (profile_status) => <span className="status">{profile_status}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, user) =>
				user.profile_status !== 'PhoneVerified' ? (
					<div className="d-flex gap-2">
						<AuthActionComponent action="user_update">
							<Link to={`view/${user.id}`}>
								<Button type="primary" className="discard" style={{ paddingBlock: '6px' }}>
									{t('COMMON.VIEW')}
								</Button>
							</Link>
							<Link to={`edit/${user.id}`}>
								<Icon name="edit" width={35} height={35} />
							</Link>
							<Button type="link" style={{ padding: '0px' }}>
								<Link to={`change-password/${user.id}`}>
									<Icon name="key" width={35} height={35} />
								</Link>
							</Button>
						</AuthActionComponent>
						<AuthActionComponent action="user_delete">
							<Button type="link" className="p-0" onClick={() => onShowConfirmation(user.id)}>
								<Icon name="delete" width={35} height={35} />
							</Button>
						</AuthActionComponent>
					</div>
				) : (
					<></>
				),
		},
	];

	const getUsers = async (page?: number, search?: string) => {
		try {
			setIsLoading(true);

			const response = await onGetUsers(page, search);
			setUsers(response.data.data.users);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetUsers(page, search.toUpperCase());
			setUsers(response.data.data.users);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (userId: number) => {
		setShowConfirmation(true);
		setTempDeletedUser(userId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteUser(tempDeletedUser as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.CLIENT_DELETED'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getUsers(currentPage);
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.CLIENT_NOT_DELETED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('USERS.TITLE')}</h3>
			</div>

			<>
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5 className="d-flex align-items-center gap-2">
								{isLoading ? <Skeleton.Node active style={{ width: 40, height: 24 }} /> : totalRecords} {t('USERS.CLIENTS')}
							</h5>
							<p>{t('USERS.MANAGE_CLIENTS')}</p>
						</div>
						<AuthActionComponent action="user_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div className="my-3">
						<Input
							style={{ width: 450, maxWidth: '100%', paddingBlock: 8 }}
							size="middle"
							placeholder={t('USERS.SEARCH')}
							onChange={(e) => setSearch(e.target.value)}
							prefix={<Icon name="search" style={{ marginInlineEnd: 10 }} />}
							value={search}
						/>
					</div>

					{isLoading ? (
						<LoadingComponent />
					) : (
						<div className="position-relative">
							<Table
								rowKey="id"
								columns={columns}
								dataSource={users}
								scroll={{ x: 400 }}
								pagination={{
									pageSize: 15,
									total: totalRecords,
									showSizeChanger: false,
									current: currentPage,
									onChange: fetchMoreData,
								}}
								locale={{ emptyText: <NoData /> }}
							/>
							{isLoadMore && (
								<div className="loadMoreContainer">
									<LoadingComponent />
								</div>
							)}
						</div>
					)}
				</div>
			</>

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('USERS.DELETE_CLIENT')}
				description={t('USERS.DELETE_MODAL_DESCRIPTION')}
				note={t('USERS.DELETE_MODAL_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListUserPage;
